import { useState, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_STORAGE_KEY = 'audit-history-tour-completed';

export function useAuditHistoryTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(() => {
    return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
  });

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setHasCompletedTour(true);
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(false);
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="audit-history-header"]',
      title: 'Welcome to Audit History',
      content: 'This is your central hub for viewing all completed and archived audits. Track past audits, review results, and access historical data.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="audit-history-search"]',
      title: 'Search Audits',
      content: 'Quickly find specific audits by searching for audit numbers, titles, or other details.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="audit-history-export"]',
      title: 'Export Options',
      content: 'Export your audit history data to CSV or Excel format for reporting and analysis. Use the right panel actions to export all or selected audits.',
      placement: 'left',
    },
    {
      target: '[data-tour="audit-history-table"]',
      title: 'Audit History Table',
      content: 'View all completed audits with details like completion dates, results, and exception counts. Click on any audit to view full details or select multiple audits using the checkboxes.',
      placement: 'top',
    },
  ];

  return {
    isTourOpen,
    hasCompletedTour,
    tourSteps,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
}
